import { FEATURE_GROUP_RULE_KIND } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.constants"

/**
 * One feature in the package's PICKED set — what the composition carries
 * for a code granted by hand, whichever list it came from (a capability
 * row, a search hit, the persisted membership). Display fields are
 * optional: a capability never shows in the specifics list, which is the
 * only place that renders them.
 */
export type FeatureGroupPickedLike = {
  Code: string
  Name?: string | null
  FeatureTypeCode?: number | null
  FeatureTypeName?: string | null
  /** The module a capability belongs to, when known — the change list names it. */
  ModuleName?: string | null
  /** A console capability (common.FeatureSurfaces) rather than a specific feature. */
  IsSurface: boolean
  /**
   * Which server checks the code, and how (common.FeatureEnforcements);
   * null when none does. See license.featureGroup.checkedBy.utils.
   */
  CheckedBy?: string | null
  CheckedHow?: string | null
}

/**
 * A surface whose text nobody has authored yet: Seed_FeatureSurfaces gives
 * such a row its code as its name, so the registry never carries a made-up
 * label. The editor shows the code and says the text is missing.
 */
export function isPlaceholderName(feature: {
  Code: string
  Name?: string | null
}) {
  return feature.Name == null || feature.Name === feature.Code
}

/** The shape every rule and rule option share: a kind and the value that fits it. */
export type FeatureGroupRuleLike = {
  RuleKindCode: number
  FeatureTypeCode?: number | null
  LayerCategoryFeatureCode?: string | null
  ModuleCode?: string | null
}

export function isTypeRule(
  rule: FeatureGroupRuleLike,
  featureTypeCode: number,
) {
  return (
    rule.RuleKindCode === FEATURE_GROUP_RULE_KIND.featureType &&
    rule.FeatureTypeCode === featureTypeCode
  )
}

export function isCategoryRule(rule: FeatureGroupRuleLike) {
  return rule.RuleKindCode === FEATURE_GROUP_RULE_KIND.layerCategory
}

/** "Everything in this console module, now and later"; any module when none is named. */
export function isModuleRule(rule: FeatureGroupRuleLike, moduleCode?: string) {
  return (
    rule.RuleKindCode === FEATURE_GROUP_RULE_KIND.module &&
    (moduleCode === undefined || rule.ModuleCode === moduleCode)
  )
}

/** One rule's identity: the kind and the one value that fits it. */
export function ruleKey(rule: FeatureGroupRuleLike) {
  return `${rule.RuleKindCode}:${rule.FeatureTypeCode ?? ""}:${rule.LayerCategoryFeatureCode ?? ""}:${rule.ModuleCode ?? ""}`
}

/** A stable signature of a whole rule set, for keying and dirty checks. */
export function ruleSignature(rules: readonly FeatureGroupRuleLike[]) {
  return rules.map(ruleKey).sort().join("|")
}

/** A rule as the set mutation takes it. */
export function toRuleInput(rule: FeatureGroupRuleLike) {
  return {
    RuleKindCode: rule.RuleKindCode,
    FeatureTypeCode: rule.FeatureTypeCode ?? null,
    LayerCategoryFeatureCode: rule.LayerCategoryFeatureCode ?? null,
    ModuleCode: rule.ModuleCode ?? null,
  }
}

export function pluralize(count: number, noun: string, plural = `${noun}s`) {
  return `${count} ${count === 1 ? noun : plural}`
}
